// Browser entry: live calculator recompute + theme toggle.
// Progressive enhancement — the page is fully usable (server-rendered defaults and
// result) before this runs. If it never loads, nothing breaks.
import { bySlug } from "./registry.js";
import { toInputs } from "./resolve.js";
import type { CalcDef, ResultLine } from "./types.js";

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;"
  );
}

function renderTicket(lines: ResultLine[]): string {
  return lines
    .map((l) => {
      const cls = "ticket-row" + (l.highlight ? " is-highlight" : "");
      const sub = l.sub ? `<span class="ticket-sub">${esc(l.sub)}</span>` : "";
      return (
        `<div class="${cls}">` +
        `<span class="ticket-label">${esc(l.label)}</span>` +
        `<span class="ticket-value">${esc(l.value)}</span>` +
        sub +
        `</div>`
      );
    })
    .join("");
}

function readForm(root: HTMLElement, def: CalcDef): Record<string, string> {
  const form: Record<string, string> = {};
  for (const f of def.fields) {
    const el = root.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${f.key}"]`);
    if (el) form[f.key] = el.value;
  }
  return form;
}

// --- Optional API (stats/feedback/contact). Empty base = disabled; the site stays static. ---
function apiBase(): string {
  const m = document.querySelector<HTMLMetaElement>('meta[name="tt-api"]');
  return (m?.content || "").trim().replace(/\/$/, "");
}

const TT_HEADERS = { "Content-Type": "application/json", "X-TT-Client": "1" };

// Fire-and-forget: never blocks the UI, never throws into the page.
function post(path: string, payload: unknown, keepalive = false): void {
  const base = apiBase();
  if (!base) return;
  try {
    void fetch(base + path, {
      method: "POST",
      headers: TT_HEADERS,
      body: JSON.stringify(payload),
      keepalive,
      mode: "cors",
      credentials: "omit",
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

function wireCalculator(root: HTMLElement) {
  const slug = root.getAttribute("data-calculator");
  if (!slug) return;
  const def = bySlug(slug);
  if (!def) return;
  const out = root.querySelector<HTMLElement>("[data-result]");
  if (!out) return;

  let lastForm: Record<string, string> = {};
  let idleTimer: number | undefined;

  const recompute = () => {
    lastForm = readForm(root, def);
    const inputs = toInputs(def, lastForm);
    const raw = def.compute(inputs);
    const lines = def.present(inputs, raw);
    out.innerHTML = renderTicket(lines);
  };

  // Send an anonymous usage event after 2s of input idle (and on blur). No-op without an API.
  const scheduleEvent = () => {
    if (!apiBase()) return;
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => post("/api/events", { slug, form: lastForm }), 2000);
  };

  root.addEventListener("input", () => { recompute(); scheduleEvent(); });
  root.addEventListener("change", () => { recompute(); scheduleEvent(); });
  root.addEventListener("focusout", () => {
    if (!apiBase()) return;
    window.clearTimeout(idleTimer);
    post("/api/events", { slug, form: lastForm }, true);
  });

  // Recompute once on load in case the browser restored different field values.
  recompute();
}

function wireFeedback() {
  document.querySelectorAll<HTMLElement>("[data-feedback]").forEach((box) => {
    const pageUrl = box.getAttribute("data-feedback") || location.pathname;
    box.querySelectorAll<HTMLButtonElement>("[data-feedback-vote]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const helpful = btn.getAttribute("data-feedback-vote") === "yes";
        post("/api/feedback", { pageUrl, helpful });
        const msg = document.createElement("p");
        msg.className = "field-help mt-2";
        msg.textContent = "Thanks for the feedback!";
        box.querySelectorAll("[data-feedback-vote]").forEach((b) => ((b as HTMLButtonElement).disabled = true));
        box.appendChild(msg);
      });
    });
  });
}

function wireContact() {
  const form = document.querySelector<HTMLFormElement>("[data-contact]");
  if (!form) return;
  const status = form.querySelector<HTMLElement>("[data-contact-status]");
  form.addEventListener("submit", (e) => {
    e.preventDefault(); // never do a raw POST from a static page
    const base = apiBase();
    const data = Object.fromEntries(new FormData(form).entries());
    const show = (text: string, ok: boolean) => {
      if (!status) return;
      status.hidden = false;
      status.textContent = text;
      status.style.color = ok ? "var(--good)" : "var(--muted)";
    };
    if (!base) {
      show("Please email us at hello@tapeandtally.com — the form isn't connected yet.", false);
      return;
    }
    fetch(base + "/api/contact", { method: "POST", headers: TT_HEADERS, body: JSON.stringify(data), mode: "cors", credentials: "omit" })
      .then((r) => r.ok ? show("Thanks — your message has been sent.", true)
                        : show("Sorry, that didn't send. Please email hello@tapeandtally.com.", false))
      .catch(() => show("Sorry, that didn't send. Please email hello@tapeandtally.com.", false));
    if (form instanceof HTMLFormElement) form.reset();
  });
}

function wireThemeToggle() {
  const btn = document.querySelector<HTMLElement>("[data-theme-toggle]");
  if (!btn) return;
  btn.addEventListener("click", () => {
    const root = document.documentElement;
    const current =
      root.getAttribute("data-theme") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    const next = current === "dark" ? "light" : "dark";
    root.setAttribute("data-theme", next);
    try {
      localStorage.setItem("tt-theme", next);
    } catch {
      /* private mode — ignore */
    }
  });
}

function wireSearch() {
  const input = document.querySelector<HTMLInputElement>("[data-search]");
  if (!input) return;
  const cards = Array.from(document.querySelectorAll<HTMLElement>("[data-calc-card]"));
  const groups = Array.from(document.querySelectorAll<HTMLElement>("[data-search-group]"));
  const empty = document.querySelector<HTMLElement>("[data-search-empty]");

  input.addEventListener("input", () => {
    const q = input.value.trim().toLowerCase();
    let anyVisible = false;
    for (const card of cards) {
      const name = card.getAttribute("data-name") || "";
      const match = q === "" || name.includes(q);
      card.hidden = !match;
      if (match) anyVisible = true;
    }
    for (const g of groups) {
      const visible = g.querySelectorAll<HTMLElement>("[data-calc-card]:not([hidden])").length > 0;
      g.hidden = !visible;
    }
    if (empty) empty.hidden = anyVisible;
  });
}

function init() {
  document.querySelectorAll<HTMLElement>("[data-calculator]").forEach(wireCalculator);
  wireThemeToggle();
  wireSearch();
  wireFeedback();
  wireContact();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
